import {Accordion, AccordionDetails, AccordionSummary, Box, Typography} from "@mui/material"
import React from "react"
import styled from "styled-components"
import {
	ExpandMore as ExpandMoreIcon,
	Email as CommunicationIcon,
	Info as InfoIcon,
	ThumbUp as ConfirmationIcon,
	Cancel as FailureIcon,
	CheckCircle as SuccessIcon,
	MonetizationOn as TransactionIcon,
	Warning as WarningIcon,
} from "@mui/icons-material"
import {green, orange, red, blue, lightGreen, grey} from "@mui/material/colors"

import {AccountActivitiesResponse, AccountActivity} from "@indebted/api"

const icons: Record<string, JSX.Element | undefined> = {
	Communication: <CommunicationIcon style={{color: grey[500]}} />,
	Confirmation: <ConfirmationIcon style={{color: lightGreen[500]}} />,
	Failure: <FailureIcon style={{color: red[500]}} />,
	Info: <InfoIcon style={{color: blue[800]}} />,
	Success: <SuccessIcon style={{color: green[500]}} />,
	Transaction: <TransactionIcon color="primary" />,
	Warning: <WarningIcon style={{color: orange[500]}} />,
}

function ActivitiesList({activities}: {activities: AccountActivitiesResponse}) {
	const [expandedPanel, setExpandedPanel] = React.useState<number | false>(false)

	const handleExpand = (panel: number) => (_: React.ChangeEvent<unknown>, currentlyExpanded: boolean) => {
		setExpandedPanel(currentlyExpanded ? panel : false)
	}

	const renderAccordionContent = (
		activity: AccountActivity,
		panelId: number,
		expanded: boolean,
		icon: JSX.Element,
	) => (
		<Accordion expanded={expanded} onChange={handleExpand(panelId)}>
			<AccordionSummary expandIcon={<ExpandMoreIcon />}>
				{icon}
				<Typography sx={{fontSize: (theme) => theme.typography.pxToRem(15), flexShrink: 0, pl: 1}}>
					<b>{activity.Title}</b> - {activity.HappenedAt}
				</Typography>
			</AccordionSummary>

			<AccordionDetails>
				{expanded && (
					<Box sx={{pl: 1.875}}>
						{activity.Fields.map((field) => (
							<Box key={field.Label}>
								<Typography sx={{fontSize: (theme) => theme.typography.pxToRem(15), flexShrink: 0}}>
									{field.Label}
								</Typography>
								<Typography
									sx={{
										fontSize: (theme) => theme.typography.pxToRem(15),
										color: "text.secondary",
										pb: 1.875,
									}}
								>
									{field.Value}
								</Typography>
							</Box>
						))}
					</Box>
				)}
			</AccordionDetails>
		</Accordion>
	)

	const renderAccordionNoContent = (activity: AccountActivity, icon: JSX.Element) => (
		<AccordionNoContent expanded={false}>
			<AccordionSummary>
				{icon}
				<Typography sx={{fontSize: (theme) => theme.typography.pxToRem(15), flexShrink: 0, pl: 1}}>
					<b>{activity.Title}</b> - {activity.HappenedAt}
				</Typography>
			</AccordionSummary>
		</AccordionNoContent>
	)

	const activityPanel = (activity: AccountActivity, panelId: number) => {
		const expanded = expandedPanel === panelId

		const hasContent = activity["Fields"].length > 0
		const icon = icons[activity.IconType] || <InfoIcon color="primary" />
		return hasContent
			? renderAccordionContent(activity, panelId, expanded, icon)
			: renderAccordionNoContent(activity, icon)
	}

	return (
		<Box sx={{width: "100%"}}>
			{activities.Activities.map((activity, panelId) => activityPanel(activity, panelId))}
		</Box>
	)
}

const AccordionNoContent = styled(Accordion)`
	.MuiAccordionSummary-content {
		cursor: default;
	}
`

export {ActivitiesList}
